import invariant from "tiny-invariant"
import {
  Big,
  type BigintIsh,
  Fraction,
  MAX_UINT128,
  Rounding,
  ZERO,
} from "../math"

import { Amount } from "./Amount"
import type { TokenType } from "./types"

/**
 * The `Share` class represents a fractional token share within the application.
 * It extends the `Fraction` class and provides specific attributes and functionality for working with token shares.
 * Instances of this class are used to represent and manipulate fractional ownership of tokens.
 */
export class Share<T extends TokenType> extends Fraction {
  /**
   * The currency associated with this share.
   * @type {TokenType}
   */
  readonly currency: T

  /**
   * The scale factor for this share based on the token's decimal places.
   * @type {bigint}
   */
  readonly scale: bigint

  /**
   * Constructs a `Share` instance representing a fractional token share.
   * @param {TokenType} currency - The token associated with the share.
   * @param {BigintIsh} numerator - The numerator of the fractional token share.
   * @param {BigintIsh} denominator - The denominator of the fractional token share.
   */
  protected constructor(
    currency: T,
    numerator: BigintIsh,
    denominator?: BigintIsh,
  ) {
    super(numerator, denominator)
    invariant(this.quotient <= MAX_UINT128, "SHARE")
    this.currency = currency
    this.scale = 10n ** BigInt(currency.decimals)
  }

  /**
   * Creates a `Share` instance from a raw share value.
   * @param {TokenType} currency - The token associated with the share.
   * @param {BigintIsh} rawShare - The raw share value.
   * @returns {Share<TokenType>} - A `Share` instance representing the raw share value.
   */
  static fromRawShare<T extends TokenType>(
    currency: T,
    rawShare: BigintIsh = 0,
  ): Share<T> {
    return new Share(currency, rawShare)
  }

  /**
   * Creates a `Share` instance from a fractional share value.
   * @param {TokenType} currency - The token associated with the share.
   * @param {BigintIsh} numerator - The numerator of the fractional token share.
   * @param {BigintIsh} denominator - The denominator of the fractional token share.
   * @returns {Share<TokenType>} - A `Share` instance representing the fractional token share.
   */
  static fromFractionalShare<T extends TokenType>(
    currency: T,
    numerator: BigintIsh,
    denominator: BigintIsh,
  ): Share<T> {
    return new Share(currency, numerator, denominator)
  }

  /**
   * Converts the `Share` into an `Amount` object based on a rebase ratio.
   * @param {{ base: bigint; elastic: bigint }} rebase - The rebase ratio.
   * @param {boolean} roundUp - Whether to round the result up or down.
   * @returns {Amount<TokenType>} - An `Amount` object representing the equivalent token amount.
   */
  toAmount(rebase: { base: bigint; elastic: bigint }, roundUp = false) {
    if (rebase.base === ZERO)
      return Amount.fromRawAmount(this.currency, this.quotient)

    const elastic = (this.quotient * rebase.elastic) / rebase.base

    if (roundUp && (elastic * rebase.base) / rebase.elastic < this.quotient) {
      return Amount.fromRawAmount(this.currency, elastic + 1n)
    }

    return Amount.fromRawAmount(this.currency, elastic)
  }

  /**
   * Adds another `Share` instance to this share.
   * @param {Share<TokenType>} other - The other `Share` instance to add.
   * @returns {Share<TokenType>} - A new `Share` instance representing the result of the addition.
   * @throws {Error} - Throws an error if the currencies of the two shares do not match.
   */
  override add(other: Share<T>): Share<T> {
    invariant(this.currency.equals(other.currency), "CURRENCY")
    const added = super.add(other)
    return Share.fromFractionalShare(
      this.currency,
      added.numerator,
      added.denominator,
    )
  }

  /**
   * Subtracts another `Share` instance from this share.
   * @param {Share<TokenType>} other - The other `Share` instance to subtract.
   * @returns {Share<TokenType>} - A new `Share` instance representing the result of the subtraction.
   * @throws {Error} - Throws an error if the currencies of the two shares do not match.
   */
  override subtract(other: Share<T>): Share<T> {
    invariant(this.currency.equals(other.currency), "CURRENCY")
    const subtracted = super.subtract(other)
    return Share.fromFractionalShare(
      this.currency,
      subtracted.numerator,
      subtracted.denominator,
    )
  }

  /**
   * Multiplies this share by a `Fraction` or a `BigintIsh` value.
   * @param {Fraction | BigintIsh} other - The value to multiply by.
   * @returns {Share<TokenType>} - A new `Share` instance representing the result of the multiplication.
   */
  override multiply(other: Fraction | BigintIsh): Share<T> {
    const multiplied = super.multiply(other)
    return Share.fromFractionalShare(
      this.currency,
      multiplied.numerator,
      multiplied.denominator,
    )
  }

  /**
   * Divides this share by a `Fraction` or a `BigintIsh` value.
   * @param {Fraction | BigintIsh} other - The value to divide by.
   * @returns {Share<TokenType>} - A new `Share` instance representing the result of the division.
   */
  override divide(other: Fraction | BigintIsh): Share<T> {
    const divided = super.divide(other)
    return Share.fromFractionalShare(
      this.currency,
      divided.numerator,
      divided.denominator,
    )
  }

  /**
   * Converts the `Share` into a string representation with a specified number of significant digits.
   * @param {number} significantDigits - The number of significant digits to display.
   * @param {object} format - Optional formatting options for the string representation.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Share`.
   */
  override toSignificant(
    significantDigits = 6,
    format?: object,
    rounding: Rounding = Rounding.ROUND_DOWN,
  ): string {
    return super
      .divide(this.scale)
      .toSignificant(significantDigits, format, rounding)
  }

  /**
   * Converts the `Share` into a string representation with a fixed number of decimal places.
   * @param {number} decimalPlaces - The number of decimal places to display.
   * @param {object} format - Optional formatting options for the string representation.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Share`.
   * @throws {Error} - Throws an error if the requested decimal places exceed the currency's decimals.
   */
  override toFixed(
    decimalPlaces: number = this.currency.decimals,
    format?: object,
    rounding: Rounding = Rounding.ROUND_DOWN,
  ): string {
    invariant(decimalPlaces <= this.currency.decimals, "DECIMALS")
    return super.divide(this.scale).toFixed(decimalPlaces, format, rounding)
  }

  /**
   * Converts the `Share` into an exact string representation without rounding.
   * @param {object} format - Optional formatting options for the string representation.
   * @returns {string} - An exact string representation of the `Share`.
   */
  toExact(format: object = { groupSeparator: "" }): string {
    Big.DP = this.currency.decimals
    return new Big(this.quotient.toString())
      .div(this.scale.toString())
      .toFormat(format)
  }
}
